import { Box, Skeleton } from "@mui/material";
import { useBreakpoint } from "@avenue-8/sidekick-ui";
import { Swiper, SwiperSlide } from "swiper/react";
import { PromptLibraryPanelSkeleton } from "./PromptLibraryPanel";
import { MOBILE_SKELETON_COUNT } from "./SocialSignalsSection";
import { MAX_SIGNALS } from "./useSocialSignals";
import {
  ScrollRow,
  SkeletonCard,
  SwiperRow,
} from "./SocialSignalsSection.styles";
import {
  FocusColumns,
  FocusRoot,
  PriorityCard,
  PriorityCardHeader,
  PriorityTitleGroup,
  RadarPanelHeader,
  RadarPanelRoot,
  RadarPanelRow,
  SecondaryColumnSlot,
  WelcomeBlock,
  WelcomeTitle,
} from "./ChatFocusView.styles";

type FocusCardRowsSkeletonProps = {
  rowCount?: number;
};

const FocusCardRowsSkeleton = ({
  rowCount = 3,
}: FocusCardRowsSkeletonProps) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      width: "100%",
      py: 0.5,
    }}
  >
    {Array.from({ length: rowCount }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={72} width="100%" />
    ))}
  </Box>
);

export const FocusPriorityCardSkeleton = ({
  rowCount = 3,
}: FocusCardRowsSkeletonProps) => (
  <>
    <PriorityCardHeader>
      <PriorityTitleGroup>
        <Skeleton width={200} height={28} />
      </PriorityTitleGroup>
      <Skeleton width="100%" height={36} />
    </PriorityCardHeader>
    <FocusCardRowsSkeleton rowCount={rowCount} />
  </>
);

const RadarSignalsPanelSkeleton = () => {
  const isLaptopDown = useBreakpoint("laptop");

  return (
    <RadarPanelRow>
      <RadarPanelRoot>
        <RadarPanelHeader>
          <Skeleton width={80} height={20} />
          <Skeleton width={220} height={28} />
          <Skeleton width="60%" height={20} />
        </RadarPanelHeader>
        {isLaptopDown ? (
          <SwiperRow>
            <Swiper spaceBetween={16} slidesPerView={1.25}>
              {Array.from({ length: MOBILE_SKELETON_COUNT }).map((_, i) => (
                <SwiperSlide key={i}>
                  <SkeletonCard $inSwiper>
                    <Skeleton variant="rounded" width="100%" height={320} />
                  </SkeletonCard>
                </SwiperSlide>
              ))}
            </Swiper>
          </SwiperRow>
        ) : (
          <ScrollRow>
            {Array.from({ length: MAX_SIGNALS }).map((_, i) => (
              <SkeletonCard key={i}>
                <Skeleton variant="rounded" width="100%" height={220} />
              </SkeletonCard>
            ))}
          </ScrollRow>
        )}
      </RadarPanelRoot>
    </RadarPanelRow>
  );
};

type ChatFocusViewSkeletonProps = {
  hasRadarModule?: boolean;
};

export const ChatFocusViewSkeleton = ({
  hasRadarModule = false,
}: ChatFocusViewSkeletonProps) => (
  <FocusRoot>
    <WelcomeBlock>
      <WelcomeTitle variant="h4">
        <Skeleton width={280} sx={{ mx: "auto" }} />
      </WelcomeTitle>
    </WelcomeBlock>
    {hasRadarModule ? (
      <RadarSignalsPanelSkeleton />
    ) : (
      <FocusColumns>
        <PriorityCard>
          <FocusPriorityCardSkeleton rowCount={4} />
        </PriorityCard>
        <SecondaryColumnSlot>
          <PromptLibraryPanelSkeleton />
        </SecondaryColumnSlot>
      </FocusColumns>
    )}
  </FocusRoot>
);
